import Vue from 'vue'
import { AxiosResponse } from 'axios'
import { User } from '@/api/Auth/auth.interface'
import { TranslateResult } from 'vue-i18n'
import { DeliveryCurrency } from './interfaces/remittance.interface'

export interface Services {
  name: string
  active: boolean
}

export interface DashboardLink {
  color: string
  icon: string
  to: { name: string }
  text: TranslateResult
  roles: string[]
}

export interface DashboardServiceLink extends DashboardLink {
  service: string
}
export interface DatePicker extends Vue {
  getData: () => { minDate: string; maxDate: string }
}

export interface VueForm extends Vue {
  validate: () => boolean
  reset: () => boolean
  resetValidation: () => void
}

export interface CustomVueForm extends VueForm {
  getData: () => object
  setData: (data: any) => void
  name: string
}

export interface VueTotal extends Vue {
  newFilter: (params: Params) => void
}
export interface VueTable extends Vue {
  addItem: (item: any) => void
  editItem: (item: any) => void
  removeItem: (item: any) => void
  refresh: () => void
  newFilter: (params: Params) => void
  data: Paginator<any>
  lastParams: Params
  lastFilter: Params
}

export interface Paginator<T> {
  count: number
  next: string | null
  prev: string | null
  results: T[]
}

export interface Params {
  [key: string]: any
}

export interface RegularExpression {
  test: (value: string) => boolean
}

export interface Body {
  uuid?: string
  [key: string]: any
}

export interface Region {
  url: string
  country: string
  name: string
  name_ascii: string
  geoname_id: number
  alternate_names: string
  display_name: string
  geoname_code: string
  id: number
}

export interface Subregion {
  url: string
  country: string
  region: string
  name: string
  name_ascii: string
  geoname_id: number
  alternate_names: string
  display_name: string
  geoname_code: number
  uuid: string
  id: number
}

export interface SubRegionCrud {
  region?: number
  subregion: number
  subregion_name: string
  fixed_commission: number
  is_active: boolean
}

export interface MessengerConfig {
  uuid?: string
  agent: string
  priority: number
  is_active: boolean
  delivery_currency: DeliveryCurrency
  subregion_commissions: SubRegionCrud[]
  commission_currency: DeliveryCurrency
}

export interface CreateMessengerConfig {
  agent: string
  priority: number
  delivery_currency: string
  commission_currency: string
  prepaid: boolean
  is_active: boolean
  subregion_commissions: SubRegionCrud[]
}

export interface Messenger extends User {
  messenger_configs: MessengerConfig[]
}

export interface City {
  url: string
  country: string
  region: string
  subregion: string
  name: string
  name_ascii: string
  geoname_id: number
  alternate_names: string
  display_name: string
  search_names: string
  latitude: number
  longitude: number
  population: number
  feature_code: string
  timezone: string
  uuid: string
  zip_code: string
}

export interface CreateCityDTO {
  name: string
  display_name: string
  subregion?: number
  region: number
  country: number
}

export type getterFunction = <T>(
  params?: Params,
) => Promise<AxiosResponse<Paginator<T>>>

export interface TableHeader<T = any> {
  text: string | TranslateResult
  value: keyof T | 'actions'
  align?: 'start' | 'center' | 'end'
  sortable?: boolean
  filterable?: boolean
  divider?: boolean
  class?: string | string[]
  width?: string | number
  notShow?: boolean
}

export interface Stepper extends Vue {
  currentComponent: VueForm
}

export interface Exchange {
  from: {
    currency: string
    amount: number
    flag: string
  }
  to: {
    currency: string
    amount: number
    flag: string
  }
}

export interface CrudUser {
  first_name: string
  last_name: string
  email: string
  username: string
  password: string
  re_password: string
  phone: string
  address: {
    address_1: ''
  }
  image: null | File
  services: string[]
  agency?: string
  email_notify: boolean
  can_create_agents?: boolean
}

export interface EditUser extends CrudUser {
  uuid: string
}

export interface Iterable {
  [key: string]: any
}

export interface Step extends Vue {
  onNext: () => false | object
}

export interface SelectItem {
  text: string | number
  value: string | number
}

export interface TableOptions {
  groupBy: string[]
  groupDesc: string[]
  itemsPerPage: number
  multiSort: boolean
  mustSort: boolean
  page: number
  sortBy: string[]
  sortDesc: string[]
}

export interface Locality {
  id: number
  name: string
  name_ascii: string
  slug: string
  geoname_id: number
  alternate_names: string
  uuid: string
  display_name: string
  search_names: string
  latitude: string
  longitude: string
  population: number
  feature_code: string
  timezone: string
  subregion: number
  region: number
  country: number
}

export enum Currencies {
  USD = 'USD',
  CUP = 'CUP',
  EUR = 'EUR',
}

export interface Country {
  id: number
  name: string
  name_ascii: string
  slug: string
  geoname_id: number
  alternate_names: string
  code2: string
  code3: string
  continent: string
  tld: string
  phone: string
}

export enum PaymentMethods {
  Zelle = 'Zelle',
  CashApp = 'CashApp',
  Cash = 'Cash',
  Other = 'Other',
}
